import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { initialValues, validationSchema } from './formData';
import { PqrAttachment } from '../../../../../shared/types/modules/pqr';
import { PqrAttachmentProps } from './props';
import { ToastSwal } from '../../../../components/general/toastSwals/toastSwal';
import { fileToBase64 } from '../../../../../shared/utils/files';
import { Attachment } from '../../../../../shared/types/shared';
import { asyncUpdatePqr } from '../../../../../services/pqr.service';
import { asyncGetAttachmentsByPqrId } from '../../../../../services/attachment.service';
import { GridColDef } from '@mui/x-data-grid';
import {
    PqrAttachmentActions,
    PqrAttachmentActionsLast,
} from '../form/attachment/actions';
import { TableTooltip } from '../../../../components/general/table';
import { DateTooltip } from '../../../../components/general/dateTooltip';

export const usePqrAttachments = ({ id }: PqrAttachmentProps) => {
    const [loading, setLoading] = useState(false);
    const [openAttachmentsModal, setOpenAttachmentsModal] = useState(false);
    const [attachments, setAttachments] = useState<Attachment[]>([]);

    useEffect(() => {
        const shouldFetch =
            Boolean((id as string)?.trim()) &&
            openAttachmentsModal &&
            attachments?.length === 0;

        if (shouldFetch) {
            setLoading(true);
            asyncGetAttachmentsByPqrId((id as string).trim())
                .then((attachments) => {
                    if (attachments) setAttachments(attachments);
                })
                .finally(() => setLoading(false));
        }
    }, [id, openAttachmentsModal]);

    useEffect(() => {
        if (!openAttachmentsModal) {
            setAttachments([]);
        }
    }, [openAttachmentsModal]);

    const handleClean = () => {
        formik?.resetForm();
    };

    const handleCloseAttachmentsModal = () => {
        setOpenAttachmentsModal(false);
        handleClean();
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: console.log,
    });

    const handleOnChange = async (files: File[]) => {
        const dataAttachment: Attachment[] = [];
        for await (const file of files) {
            const fileName = file.name.toLowerCase();
            const exists = attachments?.some((item: Attachment) =>
                item.name.toLowerCase().includes(fileName)
            );

            if (!exists) {
                const res = await fileToBase64(file);
                if (res) dataAttachment.push(res);
            } else {
                setOpenAttachmentsModal(false);
                handleClean();
                ToastSwal.fire({
                    text: `Lo sentimos, no se pudo cargar el archivo ${fileName}, ya existe previamente`,
                    icon: 'error',
                    timer: 5000,
                    position: 'center-end',
                });
            }
        }
        formik.setFieldValue('attachments', dataAttachment);
    };

    const handleSubmit = async () => {
        await formik?.submitForm();

        if (!formik.isValid) return;

        setLoading(true);

        const data: PqrAttachment = formik?.values as PqrAttachment;

        let res: any | undefined = undefined;

        try {
            res = await asyncUpdatePqr(id as string, data);
        } catch (error) {}

        if (res) {
            ToastSwal.fire({
                text: '¡Felicidades! el archivo adjunto se creó satisfactoriamente.',
                icon: 'success',
                timer: 5000,
                position: 'center-end',
            });
            setOpenAttachmentsModal(false);
            formik?.resetForm();
        } else {
            ToastSwal.fire({
                text: 'Lo sentimos, no se pudo crear el archivo adjunto. Por favor, asegúrate de diligenciar los datos correctamente e intenta nuevamente más tarde.',
                icon: 'error',
                timer: 5000,
                position: 'center-end',
            });
        }

        setLoading(false);
    };

    const columns: GridColDef<Attachment>[] = [
        {
            field: 'actions',
            headerName: 'Acciones',
            width: 100,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => <PqrAttachmentActions attachment={row.row} />,
        },
        {
            field: 'name',
            headerName: 'Nombre',
            minWidth: 150,
            flex: 0.6,
            renderCell: (row) => <TableTooltip value={row.row.name || ''} />,
        },
        {
            field: 'createdBy',
            headerName: 'Creado por',
            minWidth: 150,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip
                    value={
                        row.row.createdBy
                            ? `(${row.row.createdBy.username}) ${row.row.createdBy.name} ${row.row.createdBy.surname}`
                            : ''
                    }
                />
            ),
        },
        {
            field: 'created_at',
            headerName: 'Fecha de creación',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => (
                <DateTooltip date={row.row.created_at} showFornow />
            ),
        },
        {
            field: 'link',
            headerName: 'Web link adjunto',
            width: 100,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => (
                <PqrAttachmentActionsLast attachment={row.row} />
            ),
        },
    ];

    return {
        formik,
        handleOnChange,
        openAttachmentsModal,
        setOpenAttachmentsModal,
        handleCloseAttachmentsModal,
        handleSubmit,
        loading,
        attachments,
        columns,
    };
};
